import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "../store/store";
import { addOfflineSubmissionByCategory } from "../store/stateSlice";
import { saveSectionData } from "../Services/saveSectionData";
import { getAccessToken } from "../Services/getAccessToken";
import { 
  getInitialCycleData, 
  saveInitialCycleData, 
  validateInitialCycleData 
} from "../utils/initialCycleData";

import moment from "moment";

interface Props {
  onCycleComplete?: () => void;
  /** Label for each item (e.g. "Pack" or "NW"). Default: "Pack" */
  itemLabel?: string;
  /** Number of items (e.g. 15 for NW 1 to 15). Default: 15 */
  itemCount?: number;
  /** When set (e.g. "cr3ea_prod_pqi_bakeries"), save to this table instead of default PQI fronts table */
  sectionDataTableName?: string;
  /** When provided with sectionDataTableName=cr3ea_prod_pqi_bakeries, use Backing PQI store/service */
  backingPQISave?: {
    addOffline: (payload: { cycleNo: number; records: any[] }) => void;
    saveOnline: (records: any[]) => Promise<void>;
  };
}

const NetWeightEvaluation: React.FC<Props> = ({ onCycleComplete, itemLabel: itemLabelProp, itemCount: itemCountProp, sectionDataTableName, backingPQISave: _backingPQISave }) => {
  const itemLabel = itemLabelProp ?? 'Pack';
  const itemCount = itemCountProp ?? 15;
  const dispatch = useDispatch();
  const plantTourId = useSelector((state: RootState) => state.planTour.plantTourId);
  const user = useSelector((state: RootState) => state.user.user);
  const selectedShift = useSelector((state: RootState) => state.planTour.selectedCycle);
  const isOfflineStarted = useSelector((state: RootState) => state.appState.isOfflineStarted);
  const offlineSubmissionsByCategory = useSelector((state: RootState) => state.appState.offlineSubmissionsByCategory);
  // Get Net Weight data from shared reduxCycleData (for bidirectional sync)
  const reduxData = useSelector((state: RootState) => state.planTour.cycleData);
  
  // Local state for cycles (replacing Redux slice)
  type CycleData = {
    cycleNum: string;
    product: string;
    executiveName: string;
    standardWeight?: string;
    batchNo: string;
    packageDate: string;
    expiryDate: string;
    mc1: string[];
    mc2: string[];
    mc3: string[];
    mc4: string[];
    packs: Array<{ packNo: number; netWeight: string }>;
    packDefects?: { [packNo: number]: { category?: string; defect?: string; majorDefect?: string } };
    packStatus?: { [packNo: number]: "Okay" | "Not Okay" | null };
  };
  
  const [fetchedCycles, setFetchedCycles] = useState<CycleData[]>([]);
  const [currentCycle, setCurrentCycle] = useState<number>(1);
  const [inspectionValues, setInspectionValues] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [expandedMap, setExpandedMap] = useState<Record<number, boolean>>({});
  // Form fields state for start section
  const [formFields, setFormFields] = useState<{ [cycleNo: number]: any }>({});
  // Session started state per cycle
  const [sessionStarted, setSessionStarted] = useState<{ [cycleNo: number]: boolean }>({});
  // Pack status state (for Okay/Not Okay buttons - optional, for UI consistency)
  const [packStatus, setPackStatus] = useState<{ [cycleNo: number]: { [packNo: number]: "Okay" | "Not Okay" | null } }>({});
  // Pack defect information state
  const [packDefects, setPackDefects] = useState<{ 
    [cycleNo: number]: { 
      [packNo: number]: { 
        category?: string; 
        defect?: string; 
        majorDefect?: string; 
      } 
    } 
  }>({});

  // Multiple products per cycle: show summary and "Add New Product" / "Complete Cycle" when 1+ products saved in current cycle
  const [productFormExpandedByCycle, setProductFormExpandedByCycle] = useState<Record<number, boolean>>({});

  // Persist which cycles user has clicked "Complete Cycle" for (survives refresh)
  const STORAGE_KEY_COMPLETED = (id: string) => `netweight-completed-cycles-${id || 'default'}`;
  const [completedCyclesSet, setCompletedCyclesSet] = useState<Set<number>>(() => new Set());

  const currentCycleRef = React.useRef(currentCycle);
  currentCycleRef.current = currentCycle;


  // Process Net Weight data from reduxCycleData (for bidirectional sync with SharePoint)
  const processNetWeightData = useCallback((cycleData: any[]) => {
    console.log('NetWeightEvaluation: Processing Net Weight data from reduxCycleData', { cycleDataLength: cycleData?.length });
    if (!cycleData || cycleData.length === 0) {
      console.log('NetWeightEvaluation: No cycleData provided, skipping processing');
      return;
    }

    // Filter data for Net Weight category
    const netWeightData = cycleData.filter((item: any) => 
      item.cr3ea_category === 'Net Weight' || item.cr3ea_category === 'Net Weight Evaluation'
    );

    console.log('NetWeightEvaluation: Filtered Net Weight data', { netWeightDataLength: netWeightData.length });

    if (netWeightData.length === 0) {
      console.log('NetWeightEvaluation: No Net Weight data found in cycleData');
      return;
    }

    // Get current fetchedCycles from local state
    const currentFetchedCycles = fetchedCycles || [];

    // Group data by cycle
    const cyclesMap: { [cycleNum: string]: any } = {};
    
    netWeightData.forEach((item: any) => {
      const cycleMatch = item.cr3ea_cycle?.match(/Cycle-(\d+)/);
      if (!cycleMatch) return;
      
      const cycleNum = parseInt(cycleMatch[1]);
      const cycleKey = cycleNum.toString();
      
      if (!cyclesMap[cycleKey]) {
        // Try to preserve existing cycle data (especially MC values) if it exists
        const existingCycle = (currentFetchedCycles || []).find(c => c.cycleNum === cycleKey);
        cyclesMap[cycleKey] = {
          cycleNum: cycleKey,
          product: item.cr3ea_productname || existingCycle?.product || 'N/A',
          executiveName: item.cr3ea_executivename || existingCycle?.executiveName || 'N/A',
          standardWeight: item.cr3ea_standardntweight ?? existingCycle?.standardWeight ?? '',
          batchNo: item.cr3ea_batchno || existingCycle?.batchNo || 'N/A',
          packageDate: item.cr3ea_pkd || item.cr3ea_packeddate || existingCycle?.packageDate || 'N/A',
          expiryDate: item.cr3ea_expiry || item.cr3ea_expirydate || existingCycle?.expiryDate || 'N/A',
          mc1: existingCycle?.mc1 || [],
          mc2: existingCycle?.mc2 || [],
          mc3: existingCycle?.mc3 || [],
          mc4: existingCycle?.mc4 || [],
          packs: [],
          packDefects: {} as { [packNo: number]: { category?: string; defect?: string; majorDefect?: string } },
          packStatus: {} as { [packNo: number]: "Okay" | "Not Okay" | null }
        };
      }
      
      // Extract pack information - can come from cr3ea_packno OR cr3ea_evaluationtype (e.g., "Pack 1")
      let packNo: number | null = null;
      
      // Try to get pack number from cr3ea_packno first (Pack 1 or NW 1)
      const packMatch = item.cr3ea_packno?.match(/(?:Pack|NW)\s*(\d+)/i);
      if (packMatch) {
        packNo = parseInt(packMatch[1]);
      } else {
        // If no packno, try to extract from evaluationtype (e.g., "Pack 1", "NW 1")
        const evalMatch = item.cr3ea_evaluationtype?.match(/(?:Pack|NW)\s*(\d+)/i);
        if (evalMatch) {
          packNo = parseInt(evalMatch[1]);
        }
      }
      
      if (packNo) {
        const packData = {
          packNo,
          netWeight: item.cr3ea_netweight || '',
          criteria: item.cr3ea_criteria,
          defectCategory: item.cr3ea_defectcategory,
          defect: item.cr3ea_defect,
          defectRemarks: item.cr3ea_defectremarks
        };
        
        // Add to packs array if not already present
        const existingPack = cyclesMap[cycleKey].packs.find((p: any) => p.packNo === packNo);
        if (!existingPack) {
          cyclesMap[cycleKey].packs.push(packData);
        }
        
        // Store pack status
        if (item.cr3ea_criteria === 'Okay') {
          cyclesMap[cycleKey].packStatus[packNo] = 'Okay';
        } else if (item.cr3ea_criteria === 'Not Okay') {
          cyclesMap[cycleKey].packStatus[packNo] = 'Not Okay';
          // Store defect information
          if (item.cr3ea_defectcategory || item.cr3ea_defect) {
            cyclesMap[cycleKey].packDefects[packNo] = {
              category: item.cr3ea_defectcategory || 'Category A',
              defect: item.cr3ea_defect || '',
              majorDefect: item.cr3ea_defectremarks || ''
            };
          }
        } else if (!item.cr3ea_criteria || item.cr3ea_criteria === null) {
          // If no criteria set, but we have a pack with net weight, consider it "Okay"
          if (item.cr3ea_netweight) {
            cyclesMap[cycleKey].packStatus[packNo] = 'Okay';
          }
        }
      } else {
        // If we can't determine pack number, still mark cycle as having data (for cycle completion detection)
        console.log('NetWeightEvaluation: Could not extract pack number from record:', {
          packno: item.cr3ea_packno,
          evaluationtype: item.cr3ea_evaluationtype,
          cycle: item.cr3ea_cycle
        });
      }
    });

    // Convert cyclesMap to array and merge with existing cycles
    const cyclesFromRedux: CycleData[] = Object.values(cyclesMap);
    
    console.log('NetWeightEvaluation: Processed cycles from reduxCycleData:', {
      cyclesCount: cyclesFromRedux.length,
      cycles: cyclesFromRedux.map(c => ({
        cycleNum: c.cycleNum,
        packsCount: c.packs?.length || 0,
        product: c.product
      }))
    });
    
    if (cyclesFromRedux.length > 0) {
      // Multiple products per cycle: keep ALL local entries, then add from redux only if not already present (same cycleNum + product + batchNo)
      const mergedCycles: CycleData[] = [...(currentFetchedCycles || [])];
      const key = (c: CycleData) => `${c.cycleNum}|${(c.product || '').trim()}|${(c.batchNo || '').trim()}`;
      const existingKeys = new Set(mergedCycles.map(key));

      cyclesFromRedux.forEach(cycle => {
        const cycleKey = key(cycle);
        if (existingKeys.has(cycleKey)) {
          // Update existing entry with pack data from redux (more up-to-date)
          const idx = mergedCycles.findIndex(c => key(c) === cycleKey);
          if (idx >= 0) {
            const existing = mergedCycles[idx];
            const updated: any = {
              ...cycle,
              mc1: existing.mc1?.length ? existing.mc1 : cycle.mc1,
              mc2: existing.mc2?.length ? existing.mc2 : cycle.mc2,
              mc3: existing.mc3?.length ? existing.mc3 : cycle.mc3,
              mc4: existing.mc4?.length ? existing.mc4 : cycle.mc4,
              packs: (cycle.packs && cycle.packs.length > 0) ? cycle.packs : (existing.packs || []),
              packDefects: (cycle as any).packDefects && Object.keys((cycle as any).packDefects).length > 0 ? (cycle as any).packDefects : (existing as any).packDefects,
              packStatus: (cycle as any).packStatus && Object.keys((cycle as any).packStatus).length > 0 ? (cycle as any).packStatus : (existing as any).packStatus
            };
            mergedCycles[idx] = updated;
          }
        } else {
          // Don't merge in API data for the current active cycle so it always shows start section until user saves
          if (cycle.cycleNum !== String(currentCycleRef.current)) {
            existingKeys.add(cycleKey);
            mergedCycles.push(cycle);
          }
        }
      });

      mergedCycles.sort((a, b) => {
        const na = parseInt(a.cycleNum || '0');
        const nb = parseInt(b.cycleNum || '0');
        return na !== nb ? na - nb : ((a.product || '').localeCompare(b.product || '') || (a.batchNo || '').localeCompare(b.batchNo || ''));
      });

      setFetchedCycles(mergedCycles);
      console.log(`NetWeightEvaluation: Updated fetchedCycles with ${mergedCycles.length} entries (multi-product per cycle preserved)`);
    } else {
      console.log('NetWeightEvaluation: No cycles found to merge');
    }
  }, [fetchedCycles]);

  // Process offline submissions for Net Weight Evaluation
  // This function processes offline submissions similar to CBB and Product Evaluation
  const processOfflineData = useCallback(() => {
    console.log('NetWeightEvaluation: Processing offline data');
    
    // Get Net Weight Evaluation-specific offline submissions
    const netWeightOfflineSubmissions = offlineSubmissionsByCategory['Net Weight Evaluation'] || [];
    
    console.log('NetWeightEvaluation: Found offline submissions:', netWeightOfflineSubmissions.length);
    
    // Process each offline submission to update fetchedCycles
    const offlineCycles: CycleData[] = [];

    netWeightOfflineSubmissions.forEach(submission => {
      const cycleNo = submission.cycleNo;
      const records = submission.records || [];
      const formData = submission.formData || {};
      const packs = submission.packs || [];
      
      console.log(`NetWeightEvaluation: Processing offline submission for cycle ${cycleNo}`, {
        hasRecords: records.length > 0,
        hasPacks: packs.length > 0,
        formData
      });
      
      // Create cycle record if we have packs OR records (packs are required for display)
      if (packs.length > 0 || records.length > 0) {
        // Extract MC data from records if available
        let mc1: string[] = [];
        let mc2: string[] = [];
        let mc3: string[] = [];
        let mc4: string[] = [];
        
        if (records.length > 0) {
          const record = records[0];
          mc1 = [
            record.cr3ea_mc1inspection1,
            record.cr3ea_mc1inspection2,
            record.cr3ea_mc1inspection3,
            record.cr3ea_mc1inspection4,
            record.cr3ea_mc1inspection5
          ].filter(v => v != null && v !== '').map(v => String(v));
          mc2 = [
            record.cr3ea_mc2inspection1,
            record.cr3ea_mc2inspection2,
            record.cr3ea_mc2inspection3,
            record.cr3ea_mc2inspection4,
            record.cr3ea_mc2inspection5
          ].filter(v => v != null && v !== '').map(v => String(v));
          mc3 = [
            record.cr3ea_mc3inspection1,
            record.cr3ea_mc3inspection2,
            record.cr3ea_mc3inspection3,
            record.cr3ea_mc3inspection4,
            record.cr3ea_mc3inspection5
          ].filter(v => v != null && v !== '').map(v => String(v));
          mc4 = [
            record.cr3ea_mc4inspection1,
            record.cr3ea_mc4inspection2,
            record.cr3ea_mc4inspection3,
            record.cr3ea_mc4inspection4,
            record.cr3ea_mc4inspection5
          ].filter(v => v != null && v !== '').map(v => String(v));
        }
        
        const cycleRecord: CycleData = {
          cycleNum: cycleNo.toString(),
          product: formData.product || (records.length > 0 ? records[0].cr3ea_productname : '') || 'N/A',
          executiveName: formData.executiveName || (records.length > 0 ? records[0].cr3ea_executivename : '') || 'N/A',
          standardWeight: formData.standardWeight ?? (records.length > 0 ? records[0].cr3ea_standardntweight : '') ?? '',
          batchNo: formData.batchNo || (records.length > 0 ? records[0].cr3ea_batchno : '') || 'N/A',
          packageDate: formData.packaged || (records.length > 0 ? records[0].cr3ea_packeddate : '') || 'N/A',
          expiryDate: formData.expiry || (records.length > 0 ? records[0].cr3ea_expirydate : '') || 'N/A',
          mc1,
          mc2,
          mc3,
          mc4,
          packs: packs
        };
        
        offlineCycles.push(cycleRecord);
        console.log(`NetWeightEvaluation: Created cycle record for cycle ${cycleNo} with ${packs.length} packs`);
      } else {
        console.warn(`NetWeightEvaluation: Skipping cycle ${cycleNo} - no packs or records found`);
      }
    });
    
    if (offlineCycles.length > 0) {
      // Multiple products per cycle: use full list from offline submissions (one entry per product)
      setFetchedCycles(offlineCycles);
      console.log(`NetWeightEvaluation: Updated with ${offlineCycles.length} offline cycle/product entries`);
    } else {
      console.log('NetWeightEvaluation: No offline cycles to add');
    }
  }, [offlineSubmissionsByCategory]);

  // Effect to handle offline mode and process offline submissions
  useEffect(() => {
    console.log('NetWeightEvaluation: Offline mode useEffect triggered - isOfflineStarted:', isOfflineStarted, 'offlineSubmissionsByCategory:', offlineSubmissionsByCategory);
    if (isOfflineStarted) {
      console.log('NetWeightEvaluation: Offline mode detected, processing offline data');
      // Process offline data immediately when offline mode is detected
      processOfflineData();
    }
    // Note: When switching back to online mode, reduxData processing will handle repopulating cycles
  }, [isOfflineStarted, offlineSubmissionsByCategory, processOfflineData]);

  // Fetch and display completed cycles on mount and when plantTourId changes (only in online mode)
  useEffect(() => {
    console.log('=== NET WEIGHT EVALUATION COMPONENT MOUNTED ===');
    console.log('Plant Tour ID:', plantTourId);
    console.log('Is Offline:', isOfflineStarted);
    console.log('Fetched cycles:', fetchedCycles.length);
    
    // CRITICAL: Don't fetch if offline - this prevents API calls in offline mode
    if (isOfflineStarted) {
      console.log('NetWeightEvaluation: Offline mode detected, processing offline data instead of fetching');
      // Process offline data on mount in offline mode
      processOfflineData();
      return;
    }

    // Note: Pack data is now fetched via reduxData (from fetchCycleDetails in ProductQualityIndex)
    // MC values are saved to NetWeightMonitoringRecord table separately
    // No need to fetch pack data separately since it comes from reduxData via processNetWeightData
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantTourId, isOfflineStarted, processOfflineData]);

  // Process Net Weight data from reduxCycleData when it changes (for SharePoint → PWA sync)
  // This works in both online and offline mode (like CBB and Product Evaluation)
  // Also merge SharePoint-completed cycles into completedCyclesSet so they show in PWA completed section
  useEffect(() => {
    console.log('NetWeightEvaluation: useEffect triggered with reduxData length:', reduxData?.length, 'isOfflineStarted:', isOfflineStarted);
    
    // Process reduxCycleData in both online and offline mode (like CBB Evaluation)
    if (reduxData && reduxData.length > 0) {
      const currentDataHash = JSON.stringify(reduxData);
      const lastProcessedHash = localStorage.getItem('lastProcessedNetWeightDataHash');
      
      if (currentDataHash !== lastProcessedHash) {
        console.log('NetWeightEvaluation: Processing new Net Weight data from reduxCycleData');
        processNetWeightData(reduxData);
        localStorage.setItem('lastProcessedNetWeightDataHash', currentDataHash);
      } else {
        console.log('NetWeightEvaluation: Skipping Net Weight data processing - no changes detected');
      }

      // Merge SharePoint-completed cycles into completedCyclesSet so cycles completed in SharePoint show in PWA
      const netWeightData = reduxData.filter((item: any) => 
        item.cr3ea_category === 'Net Weight' || item.cr3ea_category === 'Net Weight Evaluation'
      );
      const sharePointCycleNumbers = new Set<number>();
      netWeightData.forEach((item: any) => {
        const cycleMatch = item.cr3ea_cycle?.match(/Cycle-(\d+)/);
        if (cycleMatch) sharePointCycleNumbers.add(parseInt(cycleMatch[1]));
      });
      if (sharePointCycleNumbers.size > 0) {
        setCompletedCyclesSet((prev) => {
          const merged = new Set([...prev, ...sharePointCycleNumbers]);
          if (plantTourId && merged.size > prev.size) {
            localStorage.setItem(STORAGE_KEY_COMPLETED(plantTourId), JSON.stringify([...merged]));
            console.log('NetWeightEvaluation: Merged SharePoint completed cycles:', [...sharePointCycleNumbers], 'total:', merged.size);
          }
          return merged;
        });
      }
    } else {
      console.log('NetWeightEvaluation: No reduxCycleData available for processing');
    }
  }, [reduxData, processNetWeightData, plantTourId]);

  // Cleanup effect to clear localStorage when plantTourId changes or component unmounts
  useEffect(() => {
    return () => {
      localStorage.removeItem('lastProcessedNetWeightDataHash');
    };
  }, [plantTourId]);

  // Clear localStorage when plantTourId changes to ensure fresh data processing
  useEffect(() => {
    localStorage.removeItem('lastProcessedNetWeightDataHash');
    console.log('NetWeightEvaluation: Cleared localStorage data hash due to plantTourId change');
  }, [plantTourId]);

  // Only set currentCycle from fetchedCycles on initial load (multiple products per cycle: we advance only on "Complete Cycle")
  const hasInitializedCurrentCycleRef = React.useRef(false);
  const skipNextCycleUpdateRef = React.useRef(false); // set true in handleSave when we add a product so we don't jump to next cycle
  useEffect(() => {
    if (skipNextCycleUpdateRef.current) {
      skipNextCycleUpdateRef.current = false;
      hasInitializedCurrentCycleRef.current = true;
      return;
    }
    const cycles = fetchedCycles || [];
    // Multiple products per cycle: never advance past current cycle if it already has products (user is still working on it)
    const productsInCurrentCycle = cycles.filter(c => parseInt(c.cycleNum || '0') === currentCycle).length;
    if (productsInCurrentCycle > 0) {
      hasInitializedCurrentCycleRef.current = true;
      return;
    }
    const completed = new Set<number>();
    cycles.forEach(c => {
      const n = parseInt(c.cycleNum || '0');
      if (n > 0) completed.add(n);
    });
    let next = 1;
    while (completed.has(next)) next++;

    if (!hasInitializedCurrentCycleRef.current && cycles.length > 0) {
      setCurrentCycle(next);
      hasInitializedCurrentCycleRef.current = true;
    }
  }, [fetchedCycles, currentCycle]);

  // Keep expansion map in sync with currentCycle (active cycle expanded)
  useEffect(() => {
    const cycles = fetchedCycles || [];
    const highestCompleted = cycles.reduce((max: number, c: any) => {
      const n = parseInt(c.cycleNum || '0') || 0;
      return Math.max(max, n);
    }, 0);
    const totalCycles = 8;
    const lastToRender = Math.min(totalCycles, Math.max(currentCycle + 1, highestCompleted));
    const newExpanded: Record<number, boolean> = {};
    for (let i = 1; i <= lastToRender; i++) {
      newExpanded[i] = (i === currentCycle);
    }
    setExpandedMap(prev => ({ ...prev, ...newExpanded }));
  }, [currentCycle, fetchedCycles]);

  // Reset initial cycle ref when plant tour changes so we recompute on new tour
  useEffect(() => {
    hasInitializedCurrentCycleRef.current = false;
  }, [plantTourId]);

  // Load persisted completed cycles on mount / when plantTourId changes (so completion survives refresh)
  useEffect(() => {
    if (!plantTourId) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY_COMPLETED(plantTourId));
      if (raw) {
        const arr = JSON.parse(raw) as number[];
        const set = new Set(Array.isArray(arr) ? arr : []);
        setCompletedCyclesSet(set);
        if (set.size > 0) {
          const maxCompleted = Math.max(...set);
          const nextCycle = maxCompleted + 1;
          setCurrentCycle(nextCycle);
          hasInitializedCurrentCycleRef.current = true;
          // So the active cycle shows start section after refresh: clear any products for that cycle from local state
          setFetchedCycles((prev) => (prev || []).filter((c) => parseInt(c.cycleNum || '0') !== nextCycle));
        }
      } else {
        setCompletedCyclesSet(new Set());
      }
    } catch (_) {
      setCompletedCyclesSet(new Set());
    }
  }, [plantTourId]);

  const handleInputChange = (key: string, value: string) => {
    setInspectionValues(prev => ({ ...prev, [key]: value }));
  };

  // Handle form field changes for start section
  const handleFormFieldChange = (cycleNo: number, field: string, value: string) => {
    setFormFields((prev) => {
      const updatedForCycle = { ...(prev[cycleNo] || {}), [field]: value };
      const next = { ...prev, [cycleNo]: updatedForCycle };

      // Persist initial cycle data to localStorage (matches CBB/Secondary/Primary/Product behavior)
      if (cycleNo === 1 && validateInitialCycleData(updatedForCycle)) {
        saveInitialCycleData(updatedForCycle);
      }

      return next;
    });
  };

  // Auto-fill start fields from initial cycle data (keeps consistency with other tabs)
  useEffect(() => {
    const initialData = getInitialCycleData();
    if (!initialData) return;

    setFormFields((prev) => {
      const next = { ...prev } as { [cycle: number]: any };
      const totalCycles = 8;
      for (let i = 1; i <= totalCycles; i++) {
        const current = next[i] || {};
        next[i] = {
          ...current,
          product: current.product || initialData.product || '',
          partyName: current.partyName || initialData.partyName || '',
          batchNo: current.batchNo || initialData.batchNo || '',
          lineNo: current.lineNo || initialData.lineNo || '',
          packaged: current.packaged || initialData.packaged || '',
          expiry: current.expiry || initialData.expiry || '',
          executiveName: current.executiveName || initialData.executiveName || '',
          standardWeight: current.standardWeight ?? (initialData as any).standardWeight ?? '',
        };
      }
      return next;
    });
  }, []);

  // Handle start session button
  const handleStart = (cycleNo: number) => {
    // Store form data in localStorage for the service to use
    const formData = formFields[cycleNo] || {};
    localStorage.setItem(`cycle-${cycleNo}-start-data`, JSON.stringify(formData));
    
    // Mark session as started
    setSessionStarted((prev) => ({ ...prev, [cycleNo]: true }));
    
    // Expand the panel
    setExpandedMap((prev) => ({ ...prev, [cycleNo]: true }));
  };

  // Multiple products per cycle: complete current cycle and move to next (persist so it survives refresh)
  const handleCompleteCycle = () => {
    const completingCycle = currentCycle;
    const nextCycle = completingCycle + 1;
    setCompletedCyclesSet((prev) => {
      const next = new Set(prev);
      next.add(completingCycle);
      try {
        if (plantTourId) {
          localStorage.setItem(STORAGE_KEY_COMPLETED(plantTourId), JSON.stringify([...next]));
        }
      } catch (_) {
        // ignore storage errors
      }
      return next;
    });
    setCurrentCycle(nextCycle);
    setExpandedMap((prev) => ({ ...prev, [completingCycle]: false, [nextCycle]: true }));
    setProductFormExpandedByCycle((prev) => ({ ...prev, [completingCycle]: false, [nextCycle]: false }));
    setSessionStarted((prev) => ({ ...prev, [completingCycle]: false }));
    setInspectionValues({});
    setPackStatus((prev) => {
      const updated = { ...prev };
      delete updated[completingCycle];
      return updated;
    });
    setPackDefects((prev) => {
      const updated = { ...prev };
      delete updated[completingCycle];
      return updated;
    });
    // So the next cycle always shows start section (not "N product(s) saved"): clear any existing entries for next cycle from local state
    setFetchedCycles((prev) => (prev || []).filter((c) => parseInt(c.cycleNum || '0') !== nextCycle));
    onCycleComplete && onCycleComplete();
  };



  const handleSave = async () => {
    setIsLoading(true);
    skipNextCycleUpdateRef.current = true; // block effect from advancing currentCycle when we add a product (set early so any subsequent effect run sees it)
    try {
      const hasAny = Object.values(inspectionValues).some(v => (v || '').toString().trim() !== '');
      if (!hasAny) {
        alert('Please enter at least one pack value');
        skipNextCycleUpdateRef.current = false;
        setIsLoading(false);
        return;
      }

      // Multiple products per cycle: allow saving multiple products in same cycle (no "already completed" block)

      if (isOfflineStarted) {
        // build offline payload for Net Weight Evaluation
        console.log('Saving Net Weight Evaluation data in offline mode to Redux...');
        
        const buildArr = (m: number) => [1,2,3,4,5].map(i => {
          const val = (inspectionValues[`mc${m}_ins${i}`] ?? '').toString().trim();
          return val === '' ? null : val;
        });
        
        // Extract pack data from inspection values (inline logic - no longer using getNetWtPacksForCycle)
        const packs: Array<{ packNo: number; netWeight: string }> = [];
        for (let i = 1; i <= itemCount; i++) {
          const group = Math.ceil(i / 5);
          const index = ((i - 1) % 5) + 1;
          const id = `mc${group}_ins${index}`;
          const netWeight = inspectionValues[id]?.trim();
          if (netWeight && netWeight !== '') {
            packs.push({ packNo: i, netWeight });
          }
        }
        packs.sort((a, b) => a.packNo - b.packNo);
        
        // Build records for the main net weight monitoring table from formFields state
        const formData = formFields[currentCycle] || {};
        const product = formData.product || "N/A";
        const partyName = (formData.partyName || "").toString().trim();
        const executiveName = formData.executiveName || user?.Name || "N/A";
        const batchNo = formData.batchNo || "N/A";
        const packageDate = formData.packaged || "N/A";
        const expiry = formData.expiry || "N/A";
        
        const mc1 = buildArr(1);
        const mc2 = buildArr(2);
        const mc3 = buildArr(3);
        const mc4 = buildArr(4);
        
        const parseNums = (arr: (string | null)[]) => arr.map(v => (v == null ? NaN : parseFloat(v)) ).filter(n => !Number.isNaN(n)) as number[];
        const avg = (nums: number[]) => nums.length ? (nums.reduce((a,b)=>a+b,0) / nums.length).toFixed(2) : null;
        
        // Import moment for date formatting
        const moment = (await import('moment')).default;
        
        const savedData = [{
          cr3ea_qualitytourid: plantTourId || '',
          cr3ea_title: 'NetWeights_' + moment().format('DD/MM/YYYY'),
          cr3ea_cycle: `Cycle-${currentCycle}`,
          cr3ea_shift: sessionStorage.getItem("shiftValue") || "shift 1",
          cr3ea_tourstartdate: moment().format('DD/MM/YYYY'),
          cr3ea_observedby: user?.Name || null,
          cr3ea_productname: product,
          cr3ea_partyname: partyName,
          cr3ea_executivename: executiveName,
          cr3ea_batchno: batchNo,
          cr3ea_expirydate: expiry,
          cr3ea_packeddate: packageDate,
          cr3ea_mc1inspection1: mc1[0] ?? null,
          cr3ea_mc1inspection2: mc1[1] ?? null,
          cr3ea_mc1inspection3: mc1[2] ?? null,
          cr3ea_mc1inspection4: mc1[3] ?? null,
          cr3ea_mc1inspection5: mc1[4] ?? null,
          cr3ea_mc1avg: avg(parseNums(mc1)),
          cr3ea_mc2inspection1: mc2[0] ?? null,
          cr3ea_mc2inspection2: mc2[1] ?? null,
          cr3ea_mc2inspection3: mc2[2] ?? null,
          cr3ea_mc2inspection4: mc2[3] ?? null,
          cr3ea_mc2inspection5: mc2[4] ?? null,
          cr3ea_mc2avg: avg(parseNums(mc2)),
          cr3ea_mc3inspection1: mc3[0] ?? null,
          cr3ea_mc3inspection2: mc3[1] ?? null,
          cr3ea_mc3inspection3: mc3[2] ?? null,
          cr3ea_mc3inspection4: mc3[3] ?? null,
          cr3ea_mc3inspection5: mc3[4] ?? null,
          cr3ea_mc3avg: avg(parseNums(mc3)),
          cr3ea_mc4inspection1: mc4[0] ?? null,
          cr3ea_mc4inspection2: mc4[1] ?? null,
          cr3ea_mc4inspection3: mc4[2] ?? null,
          cr3ea_mc4inspection4: mc4[3] ?? null,
          cr3ea_mc4inspection5: mc4[4] ?? null,
          cr3ea_mc4avg: avg(parseNums(mc4)),
          cr3ea_platform: "PWA"
        }];
        
        // Build pack records in saveSectionData format (same as other tabs)
        const packRecords = packs.map((pack) => {
          const packNo = pack.packNo;
          const currentPackStatus = packStatus[currentCycle]?.[packNo] || null;
          const currentPackDefects = packDefects[currentCycle]?.[packNo] || {};
          
          // Determine criteria based on packStatus or defect presence
          let criteria: "Okay" | "Not Okay" = currentPackStatus || "Okay";
          if (!currentPackStatus) {
            if (currentPackDefects.category || currentPackDefects.defect || currentPackDefects.majorDefect) {
              criteria = "Not Okay";
            } else {
              criteria = "Okay";
            }
          }
          
          const base = {
            cr3ea_evaluationtype: `${itemLabel} ${packNo}`,
            cr3ea_criteria: criteria,
            cr3ea_cycle: `Cycle-${currentCycle}`,
            cr3ea_title: 'QA_' + moment().format('DD/MM/YYYY'),
            cr3ea_expiry: expiry,
            cr3ea_shift: sessionStorage.getItem("shiftValue") || "shift 1",
            cr3ea_batchno: batchNo,
            cr3ea_lineno: formData.lineNo || '',
            cr3ea_category: 'Net Weight Evaluation',
            cr3ea_pkd: packageDate,
            cr3ea_tourstartdate: moment().format('DD/MM/YYYY'),
            cr3ea_productname: product,
            cr3ea_partyname: partyName,
            cr3ea_executivename: executiveName,
            cr3ea_standardntweight: (formData.standardWeight ?? '').toString().trim() || undefined,
            cr3ea_observedby: user?.Name || '',
            cr3ea_qualitytourid: plantTourId || '',
            cr3ea_packno: `${itemLabel} ${packNo}`,
            cr3ea_netweight: pack.netWeight || '',
            cr3ea_platform: 'PWA'
          };
          
          if (criteria === "Not Okay") {
            return {
              ...base,
              cr3ea_defectcategory: currentPackDefects.category || 'Category B',
              cr3ea_defect: currentPackDefects.defect || '',
              cr3ea_defectremarks: currentPackDefects.majorDefect || ''
            };
          }
          return base;
        });
        
        // Create offline submission with both main records (MC data) and pack records (for saveSectionData)
        const offlineSubmission = {
          cycleNo: currentCycle,
          records: savedData, // MC data for NetWeightMonitoringRecord table
          packRecords: packRecords, // Pack data for cr3ea_prod_pqi_fronts/bakeries table (saveSectionData format)
          packs: packs, // Keep for backward compatibility
          packDefects: packDefects[currentCycle],
          formData: {
            ...formData,
            product: product,
            partyName,
            executiveName: executiveName,
            standardWeight: formData.standardWeight ?? '',
            batchNo: batchNo,
            lineNo: formData.lineNo || '',
            packaged: packageDate,
            expiry: expiry,
            shift: sessionStorage.getItem("shiftValue") || "shift 1"
          },
          timestamp: Date.now(),
          plantTourId: plantTourId || '',
          ...(sectionDataTableName ? { sectionDataTableName } : {})
        };
        
        console.log(`[NetWeightEvaluation] Saving offline submission for cycle ${currentCycle}:`, {
          cycleNo: currentCycle,
          packRecordsCount: packRecords.length,
          packsCount: packs.length,
          samplePackRecord: packRecords[0] ? {
            evaluationType: packRecords[0].cr3ea_evaluationtype,
            cycle: packRecords[0].cr3ea_cycle,
            category: packRecords[0].cr3ea_category,
            packNo: packRecords[0].cr3ea_packno,
            productname: packRecords[0].cr3ea_productname,
            executivename: packRecords[0].cr3ea_executivename,
            qualitytourid: packRecords[0].cr3ea_qualitytourid
          } : null,
          formData: offlineSubmission.formData
        });
        
        dispatch(addOfflineSubmissionByCategory({ 
          category: 'Net Weight Evaluation', 
          submission: offlineSubmission 
        }));
        
        // Also update fetched cycles to show completed cycle
        const cycleRecord: CycleData = {
          cycleNum: currentCycle.toString(),
          product: product,
          executiveName: executiveName,
          batchNo: batchNo,
          packageDate: packageDate,
          expiryDate: expiry,
          mc1: mc1.filter(v => v !== null).map(v => String(v)),
          mc2: mc2.filter(v => v !== null).map(v => String(v)),
          mc3: mc3.filter(v => v !== null).map(v => String(v)),
          mc4: mc4.filter(v => v !== null).map(v => String(v)),
          packs: packs
        };
        
        const savedCycleNum = currentCycle; // Store before updating
        skipNextCycleUpdateRef.current = true; // don't let effect advance currentCycle when we add this product
        setFetchedCycles(prev => [...(prev || []), cycleRecord]);
        console.log('Net Weight Evaluation data saved to Redux. Total offline submissions:', (offlineSubmissionsByCategory['Net Weight Evaluation'] || []).length + 1);
        
        // Multiple products per cycle: stay on same cycle, show summary (Add New Product / Complete Cycle)
        setInspectionValues({});
        setSessionStarted(prev => ({ ...prev, [currentCycle]: false }));
        setProductFormExpandedByCycle(prev => ({ ...prev, [currentCycle]: false }));
        setPackStatus(prev => {
          const updated = { ...prev };
          delete updated[currentCycle];
          return updated;
        });
        setPackDefects(prev => {
          const updated = { ...prev };
          delete updated[currentCycle];
          return updated;
        });
        
        alert(`Product saved offline in Cycle ${savedCycleNum}. Add another product or complete cycle. Will sync when you cancel or sync offline mode.`);
        setIsLoading(false);
        return; // Exit early for offline mode - don't continue to online save logic
      } else {
        if (!plantTourId) throw new Error('No Plant Tour ID');
        
        // Store form data in localStorage for the service to use (it currently reads from localStorage)
        const formData = formFields[currentCycle] || {};
        localStorage.setItem(`cycle-${currentCycle}-start-data`, JSON.stringify(formData));
        
        const { collectEstimationDataCycleSave, saveSectionApiCall } = await import('../Services/NetWeightMonitoringRecord');
        const { savedData } = await collectEstimationDataCycleSave(currentCycle, plantTourId, user?.Name || '');
        const result = await saveSectionApiCall(savedData);
        if (!result.success) throw new Error(result.message || 'Save failed');

        // Save per-pack rows to the product PQI table for Net Weight (cr3ea_prod_pqi_fronts) using saveSectionData
        try {
          // Extract pack data from inspection values (inline logic - no longer using getNetWtPacksForCycle)
          const packs: Array<{ packNo: number; netWeight: string }> = [];
          for (let i = 1; i <= itemCount; i++) {
            const group = Math.ceil(i / 5);
            const index = ((i - 1) % 5) + 1;
            const id = `mc${group}_ins${index}`;
            const netWeight = inspectionValues[id]?.trim();
            if (netWeight && netWeight !== '') {
              packs.push({ packNo: i, netWeight });
            }
          }
          packs.sort((a, b) => a.packNo - b.packNo);
          
          if (packs.length) {
            const formData = formFields[currentCycle] || {};
            const partyNameOnline = (formData.partyName || '').toString().trim();
            const details = {
              product: formData.product || '',
              partyName: partyNameOnline,
              batchNo: formData.batchNo || '',
              lineNo: formData.lineNo || '',
              packaged: formData.packaged || '',
              expiry: formData.expiry || '',
              shift: selectedShift || sessionStorage.getItem("shiftValue") || "shift 1"
            };
            
            // Build records array in same format as other tabs (CBB, Product, etc.)
            const records = packs.map((pack) => {
              const packNo = pack.packNo;
              const currentPackStatus = packStatus[currentCycle]?.[packNo] || null;
              const currentPackDefects = packDefects[currentCycle]?.[packNo] || {};
              
              // Determine criteria based on packStatus or defect presence
              let criteria: "Okay" | "Not Okay" = currentPackStatus || "Okay";
              if (!currentPackStatus) {
                if (currentPackDefects.category || currentPackDefects.defect || currentPackDefects.majorDefect) {
                  criteria = "Not Okay";
                } else {
                  criteria = "Okay";
                }
              }
              
              const base = {
                cr3ea_evaluationtype: `${itemLabel} ${packNo}`,
                cr3ea_criteria: criteria,
                cr3ea_cycle: `Cycle-${currentCycle}`,
                cr3ea_title: 'QA_' + moment().format('DD/MM/YYYY'),
                cr3ea_expiry: details.expiry,
                cr3ea_shift: details.shift,
                cr3ea_batchno: details.batchNo,
                cr3ea_lineno: details.lineNo,
                cr3ea_category: 'Net Weight Evaluation',
                cr3ea_pkd: details.packaged,
                cr3ea_tourstartdate: moment().format('DD/MM/YYYY'),
                cr3ea_productname: details.product,
                cr3ea_partyname: details.partyName || '',
                cr3ea_executivename: formFields[currentCycle]?.executiveName || '',
                cr3ea_standardntweight: (formFields[currentCycle]?.standardWeight ?? '').toString().trim() || undefined,
                cr3ea_observedby: user?.Name || '',
                cr3ea_qualitytourid: plantTourId,
                cr3ea_packno: `${itemLabel} ${packNo}`,
                cr3ea_netweight: pack.netWeight || '',
                cr3ea_platform: 'PWA'
              };
              
              if (criteria === "Not Okay") {
                return {
                  ...base,
                  cr3ea_defectcategory: currentPackDefects.category || 'Category B',
                  cr3ea_defect: currentPackDefects.defect || '',
                  cr3ea_defectremarks: currentPackDefects.majorDefect || ''
                };
              }
              return base;
            });
            
            // Get access token and save using saveSectionData
            const tokenResult = await getAccessToken();
            const accessToken = tokenResult?.token;
            if (!accessToken) {
              console.warn('No access token available for saving pack records');
            } else {
              await saveSectionData(accessToken, records);
              console.log(`NetWeightEvaluation: Saved ${records.length} pack records using saveSectionData`);
            }
          }
        } catch (err) {
          console.warn('Saving per-pack rows failed:', err);
        }

        // Wait a moment for database to update before refreshing
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Call onCycleComplete AFTER save completes to trigger ProductQualityIndex refresh (updates reduxCycleData)
        // This ensures bidirectional sync - PWA → SharePoint
        if (onCycleComplete) {
          console.log('NetWeightEvaluation: Calling onCycleComplete to trigger ProductQualityIndex refresh');
          await onCycleComplete();
        }

        // Optimistic update: add this product to fetchedCycles so "N product(s) saved" updates immediately
        const formDataForCycle = formFields[currentCycle] || {};
        const packsForCycle: Array<{ packNo: number; netWeight: string }> = [];
        for (let i = 1; i <= itemCount; i++) {
          const group = Math.ceil(i / 5);
          const index = ((i - 1) % 5) + 1;
          const id = `mc${group}_ins${index}`;
          const netWeight = inspectionValues[id]?.trim();
          if (netWeight && netWeight !== '') {
            packsForCycle.push({ packNo: i, netWeight });
          }
        }
        packsForCycle.sort((a, b) => a.packNo - b.packNo);
        const buildArrOnline = (m: number) => [1, 2, 3, 4, 5].map(i => {
          const val = (inspectionValues[`mc${m}_ins${i}`] ?? '').toString().trim();
          return val === '' ? null : val;
        });
        const mc1o = buildArrOnline(1).filter(v => v !== null).map(v => String(v));
        const mc2o = buildArrOnline(2).filter(v => v !== null).map(v => String(v));
        const mc3o = buildArrOnline(3).filter(v => v !== null).map(v => String(v));
        const mc4o = buildArrOnline(4).filter(v => v !== null).map(v => String(v));
        const cycleRecordOnline: CycleData = {
          cycleNum: currentCycle.toString(),
          product: formDataForCycle.product || 'N/A',
          executiveName: formDataForCycle.executiveName || user?.Name || 'N/A',
          batchNo: formDataForCycle.batchNo || 'N/A',
          packageDate: formDataForCycle.packaged || 'N/A',
          expiryDate: formDataForCycle.expiry || 'N/A',
          mc1: mc1o,
          mc2: mc2o,
          mc3: mc3o,
          mc4: mc4o,
          packs: packsForCycle
        };
        skipNextCycleUpdateRef.current = true; // don't let effect advance currentCycle when we add this product
        setFetchedCycles(prev => [...(prev || []), cycleRecordOnline]);

        alert(`Product saved successfully in Cycle ${currentCycle}. Add another product or complete cycle.`);
      }

      // Multiple products per cycle: stay on same cycle, show summary
      setInspectionValues({});
      setSessionStarted(prev => ({ ...prev, [currentCycle]: false }));
      setProductFormExpandedByCycle(prev => ({ ...prev, [currentCycle]: false }));
      setPackStatus(prev => {
        const updated = { ...prev };
        delete updated[currentCycle];
        return updated;
      });
      setPackDefects(prev => {
        const updated = { ...prev };
        delete updated[currentCycle];
        return updated;
      });
    } catch (err) {
      console.error('Error saving net weight data', err);
      alert('Error saving data. See console for details.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle pack status change (Okay/Not Okay)
  const handlePackStatusChange = (cycleNum: number, packNo: number, status: "Okay" | "Not Okay") => {
    setPackStatus((prev) => ({
      ...prev,
      [cycleNum]: {
        ...(prev[cycleNum] || {}),
        [packNo]: status,
      },
    }));
    
    // Clear defect info if status changes to "Okay"
    if (status === "Okay") {
      setPackDefects((prev) => {
        const updated = { ...prev };
        if (updated[cycleNum] && updated[cycleNum][packNo]) {
          const cycleDefects = { ...updated[cycleNum] };
          delete cycleDefects[packNo];
          updated[cycleNum] = cycleDefects;
        }
        return updated;
      });
    }
  };

  // Handle pack defect field updates
  const handlePackDefectChange = (cycleNum: number, packNo: number, field: "category" | "defect" | "majorDefect", value: string) => {
    setPackDefects((prev) => ({
      ...prev,
      [cycleNum]: {
        ...(prev[cycleNum] || {}),
        [packNo]: {
          ...(prev[cycleNum]?.[packNo] || {}),
          [field]: value,
        },
      },
    }));
  };

  const renderPackList = (cycleNum: number, editable: boolean, valuesOverride?: Record<string, string>) => {
    const packs: React.ReactNode[] = [];
    for (let i = 1; i <= itemCount; i++) {
      const group = Math.ceil(i / 5);
      const index = ((i - 1) % 5) + 1;
      const id = `mc${group}_ins${index}`;
      const elementId = `mc${group}-inspection-${index}-${cycleNum}`;
      const value = (valuesOverride ? (valuesOverride[id] ?? '') : (inspectionValues[id] ?? ''));
      const currentStatus = packStatus[cycleNum]?.[i] || null;

      const currentDefect = packDefects[cycleNum]?.[i] || {};

      packs.push(
        <div key={i} className="border p-4 rounded">
          <div className="flex justify-between items-center mb-2 gap-4">
            <div className="flex items-center gap-3 flex-1">
              <h3 className="font-semibold">{itemLabel} {i}</h3>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Net Weight:</label>
                <input
                  id={elementId}
                  type="text"
                  className="w-32 border rounded px-3 py-1 text-sm"
                  placeholder={itemLabel === 'NW' ? 'Enter net weight' : 'Enter Net Weight'}
                  value={value}
                  onChange={e => editable && handleInputChange(id, e.target.value)}
                  disabled={!editable}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => editable && handlePackStatusChange(cycleNum, i, "Not Okay")}
                className={`px-3 py-1 rounded border ${currentStatus === "Not Okay"
                  ? "bg-red-100 border-red-600 text-red-600"
                  : "border-red-400 text-red-500"
                  } ${!editable ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={!editable}
              >
                Not Okay
              </button>
              <button
                onClick={() => editable && handlePackStatusChange(cycleNum, i, "Okay")}
                className={`px-3 py-1 rounded border ${currentStatus === "Okay"
                  ? "bg-green-100 border-green-600 text-green-600"
                  : "border-green-400 text-green-500"
                  } ${!editable ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={!editable}
              >
                Okay
              </button>
            </div>
          </div>
          {currentStatus === "Not Okay" && editable && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Select Category</label>
                <select
                  className="w-full border border-gray-300 rounded px-3 py-2 text-gray-900 bg-white"
                  value={currentDefect.category || ""}
                  onChange={(e) => handlePackDefectChange(cycleNum, i, "category", e.target.value)}
                >
                  <option value="">Select</option>
                  <option value="Category A">Category A</option>
                  <option value="Category B">Category B</option>
                  <option value="Category C">Category C</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">Defect</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded px-3 py-2 text-gray-900 bg-white"
                  placeholder="Enter Defect"
                  value={currentDefect.defect || ""}
                  onChange={(e) => handlePackDefectChange(cycleNum, i, "defect", e.target.value)}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium mb-1 text-gray-700">Major Defect and Remarks</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded px-3 py-2 text-gray-900 bg-white"
                  placeholder="Enter Major Defect or Remarks"
                  value={currentDefect.majorDefect || ""}
                  onChange={(e) => handlePackDefectChange(cycleNum, i, "majorDefect", e.target.value)}
                />
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {packs}
      </div>
    );
  };

  const renderCyclePanel = (n: number) => {
    // Multiple products per cycle: completed = user clicked Complete Cycle (persisted so it survives refresh)
    const isCompleted = completedCyclesSet.has(n);
    const isActive = n === currentCycle;
    const currentCycleProductCount = (fetchedCycles || []).filter(c => parseInt(c.cycleNum || '0') === currentCycle).length;
    const showProductSummary = isActive && currentCycleProductCount >= 1 && !productFormExpandedByCycle[n];

    const panelClasses = isActive
      ? 'bg-white rounded-lg shadow-sm p-4 border border-gray-200'
      : isCompleted
        ? 'bg-white rounded-lg p-4 border border-gray-200'
        : 'bg-gray-50 rounded-lg py-2 px-4 border border-gray-200';

    const expanded = expandedMap[n] ?? isActive;

    // Calculate summary counts for completed cycles (aggregate across all products in this cycle)
    let defectCount = 0;
    let okayCount = 0;
    let missedCount = 0;
    const savedCyclesForN = (fetchedCycles || []).filter(c => parseInt(c.cycleNum || '0') === n);
    const productCountForCycle = savedCyclesForN.length;
    const netWeightOfflineSubmissions = offlineSubmissionsByCategory['Net Weight Evaluation'] || [];
    const offlineSubsForCycleN = netWeightOfflineSubmissions.filter((sub: any) => sub.cycleNo === n);

    if (isCompleted && savedCyclesForN.length > 0) {
      savedCyclesForN.forEach((saved: any, idx: number) => {
        const cyclePackDefects = offlineSubsForCycleN[idx]?.packDefects || saved.packDefects || {};
        const packCriteriaMap: { [packNo: number]: string } = {};
        const packs = saved.packs && Array.isArray(saved.packs) ? saved.packs : [];
        packs.forEach((pack: any) => {
          if (pack.packNo && pack.criteria === "Not Okay") packCriteriaMap[pack.packNo] = pack.criteria;
        });
        const savedPackNos = new Set(packs.map((p: any) => p.packNo));
        for (let packNo = 1; packNo <= itemCount; packNo++) {
          const defectData = cyclePackDefects[packNo];
          const packCriteria = packCriteriaMap[packNo];
          const packExists = savedPackNos.has(packNo);
          if (defectData || packCriteria === "Not Okay") defectCount++;
          else if (packExists) okayCount++;
          else missedCount++;
        }
      });
    }

    return (
      <div key={n} className={panelClasses} aria-disabled={!isActive && !isCompleted}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3 flex-1">
            <h3 className="text-md font-semibold">
              Cycle {n}
              {isCompleted && productCountForCycle > 1 && (
                <span className="text-gray-500 font-normal ml-1">({productCountForCycle} products)</span>
              )}
            </h3>
            {isCompleted && (
              <div className="flex items-center gap-2">
                <span className="text-green-600 font-semibold">✓ Completed</span>
                {defectCount > 0 && (
                  <span className="text-red-600 bg-red-100 px-2 py-0.5 rounded-full text-xs font-medium">
                    {defectCount} Defect(s)
                  </span>
                )}
                {okayCount > 0 && (
                  <span className="text-green-600 bg-green-100 px-2 py-0.5 rounded-full text-xs font-medium">
                    {okayCount} Okay(s)
                  </span>
                )}
                {missedCount > 0 && (
                  <span className="text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded-full text-xs font-medium">
                    {missedCount} Missed
                  </span>
                )}
              </div>
            )}
          </div>
          <button
            aria-expanded={expanded}
            onClick={() => setExpandedMap(prev => ({ ...prev, [n]: !prev[n] }))}
            className="text-sm text-gray-500 hover:text-gray-700 focus:outline-none"
            aria-label={`${expanded ? 'Collapse' : 'Expand'} cycle ${n}`}
          >
            <svg className={`w-5 h-5 transform transition-transform ${expanded ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {expanded ? (
          isActive ? (
            <div>
              {/* Multiple products per cycle: show summary and Add New Product / Complete Cycle when 1+ products saved */}
              {showProductSummary ? (
                <div className="mb-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex flex-wrap gap-2 mb-2">
                      <span className="text-green-700 font-semibold">
                        {currentCycleProductCount} product(s) saved in Cycle {n}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <button
                        type="button"
                        onClick={() => {
                          setProductFormExpandedByCycle((prev) => ({ ...prev, [n]: true }));
                          setSessionStarted((prev) => ({ ...prev, [n]: false }));
                          setInspectionValues({});
                          setPackStatus((prev) => {
                            const updated = { ...prev };
                            delete updated[n];
                            return updated;
                          });
                          setPackDefects((prev) => {
                            const updated = { ...prev };
                            delete updated[n];
                            return updated;
                          });
                        }}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
                      >
                        Add New Product
                      </button>
                      <button
                        type="button"
                        onClick={handleCompleteCycle}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium"
                      >
                        Complete Cycle & Move to Next
                      </button>
                    </div>
                  </div>
                </div>
              ) : !sessionStarted[n] ? (
                <div className="space-y-6">
                  {/* Form Fields */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700">Product</label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-gray-900 bg-white"
                        placeholder="Enter Product"
                        value={formFields[n]?.product || ''}
                        onChange={e => handleFormFieldChange(n, 'product', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700">Party Name</label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-gray-900 bg-white"
                        placeholder="Enter Party Name"
                        value={formFields[n]?.partyName || ''}
                        onChange={e => handleFormFieldChange(n, 'partyName', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700">Batch No</label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-gray-900 bg-white"
                        placeholder="Enter Batch No"
                        value={formFields[n]?.batchNo || ''}
                        onChange={e => handleFormFieldChange(n, 'batchNo', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700">Executive Name</label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-gray-900 bg-white"
                        placeholder="Enter Executive Name"
                        value={formFields[n]?.executiveName || ''}
                        onChange={e => handleFormFieldChange(n, 'executiveName', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700">Standard Weight</label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-gray-900 bg-white"
                        placeholder="Enter Standard Weight"
                        value={formFields[n]?.standardWeight ?? ''}
                        onChange={e => handleFormFieldChange(n, 'standardWeight', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700">Line No</label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-gray-900 bg-white"
                        placeholder="Enter Line No"
                        value={formFields[n]?.lineNo || ''}
                        onChange={e => handleFormFieldChange(n, 'lineNo', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700">Packaged</label>
                      <input
                        type="date"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-gray-900 bg-white"
                        value={formFields[n]?.packaged || ''}
                        onChange={e => handleFormFieldChange(n, 'packaged', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700">Expiry</label>
                      <input
                        type="date"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-gray-900 bg-white"
                        value={formFields[n]?.expiry || ''}
                        onChange={e => handleFormFieldChange(n, 'expiry', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Start Button */}
                  <div className="flex justify-end">
                    <button
                      onClick={() => handleStart(n)}
                      className="px-4 py-2 bg-blue-600 text-white rounded"
                    >
                      Start Session
                    </button>
                  </div>
                </div>
              ) : (
                /* Show pack list if session started */
                <>
                  <div className="space-y-6 mt-4">
                    {renderPackList(n, true)}
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setSessionStarted(prev => ({ ...prev, [n]: false }));
                        setInspectionValues({});
                        setPackStatus(prev => {
                          const updated = { ...prev };
                          delete updated[n];
                          return updated;
                        });
                        setPackDefects(prev => {
                          const updated = { ...prev };
                          delete updated[n];
                          return updated;
                        });
                      }}
                      className="px-4 py-2 border rounded"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={isLoading}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white rounded-md"
                    >{isLoading ? 'Saving...' : 'Save Session'}</button>
                  </div>
                </>
              )}
            </div>
          ) : isCompleted ? (
            (() => {
              if (savedCyclesForN.length === 0) return <div className="text-sm text-gray-700">No saved data for this cycle.</div>;

              // Completed screen: render each product in this cycle (multiple products supported)
              return (
                <div className="space-y-6">
                  {savedCyclesForN.map((saved: any, productIndex: number) => {
                    const cyclePackDefects = { ...(offlineSubsForCycleN[productIndex]?.packDefects || saved.packDefects || {}) };
              
              // Also check saved packs for criteria field (direct from database records)
              // If a pack has cr3ea_criteria === "Not Okay", it's a defect
              const packCriteriaMap: { [packNo: number]: string } = {};
              const savedPacks = saved.packs && Array.isArray(saved.packs) ? saved.packs : [];
              if (savedPacks.length > 0) {
                savedPacks.forEach((pack: any) => {
                  if (pack.packNo) {
                    // Check if pack has criteria field
                    if (pack.criteria === "Not Okay") {
                      packCriteriaMap[pack.packNo] = pack.criteria;
                      // If pack is "Not Okay" but not in defect map, add it
                      if (!cyclePackDefects[pack.packNo]) {
                        cyclePackDefects[pack.packNo] = {
                          category: pack.defectCategory || 'Category A',
                          defect: pack.defect || '',
                          majorDefect: pack.defectRemarks || ''
                        };
                      }
                    }
                    // Also check if pack has defect fields directly
                    if (pack.defectCategory && !cyclePackDefects[pack.packNo]) {
                      cyclePackDefects[pack.packNo] = {
                        category: pack.defectCategory,
                        defect: pack.defect || '',
                        majorDefect: pack.defectRemarks || ''
                      };
                    }
                  }
                });
              }
              
              // Build list of packs from saved data
              const savedPackNos = new Set(savedPacks.map((p: any) => p.packNo));
              
              // Build defects, okays, and missed lists
              const defects: Array<{ packNo: number; category: string; defect: string; majorDefect: string }> = [];
              const okays: number[] = [];
              const missed: number[] = [];
              
              // Check all 15 packs
              for (let packNo = 1; packNo <= itemCount; packNo++) {
                const defectData = cyclePackDefects[packNo];
                const packCriteria = packCriteriaMap[packNo];
                const packExists = savedPackNos.has(packNo);
                
                // Pack is a defect if:
                // 1. It has defect data in cyclePackDefects
                // 2. OR it has criteria === "Not Okay" in the database
                if (defectData || packCriteria === "Not Okay") {
                  // Pack has defect data or is marked "Not Okay", so it's a defect
                  defects.push({
                    packNo,
                    category: defectData?.category || 'Category A',
                    defect: defectData?.defect || 'N/A',
                    majorDefect: defectData?.majorDefect || 'N/A'
                  });
                } else if (packExists) {
                  // Pack exists but no defect data and not "Not Okay", so it's "Okay"
                  okays.push(packNo);
                } else {
                  // Pack doesn't exist, so it's "Missed"
                  missed.push(packNo);
                }
              }

              // Calculate statistics for the cycle
              const packsWithWeight = savedPacks.filter((p: any) => {
                const weight = parseFloat(p.netWeight || '0');
                return !isNaN(weight) && weight > 0;
              });
              
              // Calculate average net weight
              let averageNetWeight = 0;
              if (packsWithWeight.length > 0) {
                const totalWeight = packsWithWeight.reduce((sum: number, p: any) => {
                  return sum + parseFloat(p.netWeight || '0');
                }, 0);
                averageNetWeight = totalWeight / packsWithWeight.length;
              }

              // Standard weight entered in the start section for this cycle/product
              const standardWeightValue = parseFloat(saved.standardWeight || '0');
              
              // Calculate give away percentage based on standard weight:
              // ((Average of Net Packs - standardWeight) / standardWeight) * 100
              const giveAwayPercentage =
                averageNetWeight > 0 && !isNaN(standardWeightValue) && standardWeightValue > 0
                  ? ((averageNetWeight - standardWeightValue) / standardWeightValue) * 100
                  : 0;

                    return (
                      <div key={productIndex} className="border border-slate-200 rounded-xl bg-slate-50/50 overflow-hidden">
                        <div className="bg-white border-b border-slate-200 px-4 py-3">
                          <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-800">
                            <span className="text-slate-500">Product {productIndex + 1}:</span>
                            <span>{saved.product || 'N/A'}</span>
                            <span className="text-slate-500 font-normal">Batch: {saved.batchNo || 'N/A'}</span>
                            {saved.executiveName && <span className="text-slate-500 font-normal">{saved.executiveName}</span>}
                          </div>
                        </div>
                        <div className="p-4 space-y-4">
                  <div className="mb-4">
                    <div className="text-red-600 font-semibold mb-2">Defects</div>
                    <div className="overflow-x-auto border rounded-md bg-white">
                      <table className="w-full text-sm">
                        <thead className="bg-red-50">
                          <tr>
                            <th className="px-4 py-2 border font-medium text-left">Pack No</th>
                            <th className="px-4 py-2 border font-medium text-left">Defect Category</th>
                            <th className="px-4 py-2 border font-medium text-left">Defect</th>
                            <th className="px-4 py-2 border font-medium text-left">Major Defect</th>
                          </tr>
                        </thead>
                        <tbody>
                          {defects.length > 0 ? (
                            defects.map((defect, index) => (
                              <tr key={index} className="bg-white hover:bg-gray-50">
                                <td className="px-4 py-2 border">{itemLabel} {defect.packNo}</td>
                                <td className="px-4 py-2 border">{defect.category}</td>
                                <td className="px-4 py-2 border">{defect.defect}</td>
                                <td className="px-4 py-2 border">{defect.majorDefect}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={4} className="px-4 py-2 border text-center text-gray-500">
                                No defects
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Statistics Bar */}
                  <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-3 sm:p-4 shadow-sm">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3">
                      <div className="text-sm sm:text-base font-medium text-gray-900">
                        Average of Net Packs: <span className="font-semibold">{averageNetWeight.toFixed(2)}</span>
                      </div>
                      <div className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm sm:text-base font-medium shadow-sm">
                        Give away %: {giveAwayPercentage >= 0 ? '+' : ''}{giveAwayPercentage.toFixed(2)}%
                      </div>
                    </div>
                  </div>

                  {/* Okays Section */}
                  <div className="light-surface bg-white border rounded-md p-4">
                    <div className="text-green-600 font-semibold mb-2">Okays</div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm font-medium mb-1">No Okays</div>
                        <div className="text-sm text-gray-600">
                          {okays.length > 0 ? okays.map(p => `${itemLabel} ${p}`).join(', ') : ''}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-medium mb-1">Missed</div>
                        <div className="text-sm text-gray-600">
                          {missed.length > 0 ? missed.map(p => `${itemLabel} ${p}`).join(', ') : ''}
                        </div>
                      </div>
                    </div>
                  </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()
          ) : (
            <div className="h-4" aria-hidden="true" />
          )
        ) : null}
      </div>
    );
  };

  const totalCycles = 8; // Net Weight supports up to 8 cycles

  const highestCompleted = (fetchedCycles || []).reduce((max: number, c: any) => {
    const n = parseInt(c.cycleNum || '0') || 0;
    return Math.max(max, n);
  }, 0);

  // Render completed cycles and the active cycle plus its immediate next (but don't go past totalCycles)
  const lastToRender = Math.min(totalCycles, Math.max(currentCycle + 1, highestCompleted));

  const panels: React.ReactNode[] = [];
  for (let i = 1; i <= lastToRender; i++) {
    panels.push(renderCyclePanel(i));
  }

  return (
    <div className="space-y-4">
      {panels}
    </div>
  );
};

export default NetWeightEvaluation;
